import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { widgetApiService } from '../services/widgetApiService';
import { widgetAnalyticsService } from '../services/widgetAnalyticsService';
import '../styles/widget.css';

// Import coordinate system utilities
import { getHotspotStyle } from '../utils/coordinateSystem';
import ProductModal from './ProductModal';

// Memoized hotspot component
const WidgetHotspot = React.memo(({ hotspot, side, pageContainerRefs, hoveredHotspot, onHover, onLeave, onClick }) => {
  const pageContainer = pageContainerRefs?.current?.[side];
  const style = pageContainer
    ? getHotspotStyle(hotspot, pageContainer)
    : { left: '0px', top: '0px', width: '200px', height: '200px' };
  
  return (
    <div
      className="cf-hotspot"
      style={style}
      onMouseEnter={onHover}
      onMouseLeave={onLeave}
      onClick={onClick}
    >
      <div className="cf-hotspot-marker" />

      {hoveredHotspot === hotspot?.id && (
        <div className="cf-hotspot-tooltip">
          <div className="cf-tooltip-icon">
            {hotspot?.type === 'buy' ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <circle cx="9" cy="21" r="1"></circle>
                <circle cx="20" cy="21" r="1"></circle>
                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                <polyline points="15 3 21 3 21 9"></polyline>
                <line x1="10" y1="14" x2="21" y2="3"></line>
              </svg>
            )}
          </div>
          <div className="cf-tooltip-content">
            <p className="cf-tooltip-title">{hotspot?.title}</p>
            {hotspot?.description && (
              <p className="cf-tooltip-description">{hotspot?.description}</p>
            )}
            {hotspot?.type === 'buy' && hotspot?.price && (
              <p className="cf-tooltip-price">{hotspot?.price}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
});

WidgetHotspot.displayName = 'WidgetHotspot';

const EmbeddableFlipbook = ({ catalogId, config = {} }) => {
  const { t } = useTranslation();
  const [catalog, setCatalog] = useState(null);
  const [currentPage, setCurrentPage] = useState(config?.initialPage || 1);
  const [isFlipping, setIsFlipping] = useState(false);
  const [flipDirection, setFlipDirection] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [hoveredHotspot, setHoveredHotspot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imageLoading, setImageLoading] = useState({ left: false, right: false });
  const [isPageTransitioning, setIsPageTransitioning] = useState(false);
  // Spread entrante para animación slider (pre-renderizado simultáneamente)
  const [incomingSpreadData, setIncomingSpreadData] = useState(null);
  const [productModalOpen, setProductModalOpen] = useState(false);
  const [selectedProductSku, setSelectedProductSku] = useState(null);
  const [selectedProductStoreUrl, setSelectedProductStoreUrl] = useState(null);
  const viewerRef = useRef(null);
  const pageViewTracked = useRef(new Set());
  const pageContainerRefs = useRef({ left: null, right: null });
  const preloadedImages = useRef(new Map());
  const flipTimeoutRef = useRef(null);
  const fetchAbortControllerRef = useRef(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Clear pending flip timeout
      if (flipTimeoutRef?.current) {
        clearTimeout(flipTimeoutRef?.current);
        flipTimeoutRef.current = null;
      }
      // Abort any in-flight catalog fetch
      if (fetchAbortControllerRef?.current) {
        fetchAbortControllerRef?.current?.abort();
        fetchAbortControllerRef.current = null;
      }
      // Nullificar src de cada Image para liberar memoria antes de limpiar el Map
      preloadedImages.current.forEach(img => { img.src = ''; });
      preloadedImages.current.clear();
      pageViewTracked?.current?.clear();
      // Null out DOM refs
      pageContainerRefs.current.left = null;
      pageContainerRefs.current.right = null;
    };
  }, []);

  // Initialize analytics
  useEffect(() => {
    if (config?.analyticsEnabled && config?.ga4Id) {
      widgetAnalyticsService?.init(config?.ga4Id);
    }
  }, [config?.analyticsEnabled, config?.ga4Id]);

  // Fetch catalog data
  useEffect(() => {
    const fetchCatalog = async () => {
      const controller = new AbortController();
      fetchAbortControllerRef.current = controller;

      try {
        setLoading(true);
        
        const data = await widgetApiService?.getCatalog(config?.catalogId, config?.apiUrl, config?.apiKey);

        if (controller?.signal?.aborted) return;

        // Check if catalog is published
        if (data?.status !== 'published') {
          throw new Error('This catalog is not available for public viewing.');
        }
        
        setCatalog(data);

        // Track catalog view
        if (config?.analyticsEnabled) {
          widgetAnalyticsService?.trackCatalogView(
            data?.id,
            data?.title,
            data?.companyId
          );
        }

        setError(null);
      } catch (err) {
        if (err?.name === 'AbortError') return;
        console.error('[WIDGET] Error loading catalog:', err);
        setError(err?.message || 'Failed to load catalog. Please try again later.');
      } finally {
        if (!controller?.signal?.aborted) {
          setLoading(false);
        }
      }
    };

    if (config?.catalogId) {
      fetchCatalog();
    }

    return () => {
      if (fetchAbortControllerRef?.current) {
        fetchAbortControllerRef?.current?.abort();
      }
    };
  }, [config?.catalogId, config?.apiUrl, config?.apiKey, config?.analyticsEnabled]);

  // Track page views
  useEffect(() => {
    if (catalog && config?.analyticsEnabled && !pageViewTracked?.current?.has(currentPage)) {
      widgetAnalyticsService?.trackPageView(
        catalog?.id,
        currentPage + 1,
        catalog?.companyId
      );
      pageViewTracked?.current?.add(currentPage);
    }
  }, [currentPage, catalog, config?.analyticsEnabled]);

  // Calcula el spread para cualquier número de página
  const getSpreadForPage = useCallback((pageNum) => {
    if (!catalog?.pages || !pageNum) return { left: null, right: null };
    const totalPages = catalog?.pages?.length || 0;
    const p = pageNum === 0 ? 1 : pageNum;
    if (p === 1) return { left: null, right: catalog?.pages?.[0] };
    if (p === totalPages) return { left: catalog?.pages?.[totalPages - 1], right: null };
    const leftIndex = p % 2 === 0 ? p - 1 : p - 2;
    const rightIndex = leftIndex + 1;
    return {
      left: catalog?.pages?.[leftIndex],
      right: rightIndex < totalPages ? catalog?.pages?.[rightIndex] : null,
    };
  }, [catalog?.pages]);

  // Memoized display pages calculation
  const getDisplayPages = useCallback(() => {
    if (!catalog?.pages) return { left: null, right: null };
    
    const totalPages = catalog?.pages?.length || 0;
    const isFrontCover = currentPage === 1;
    const isBackCover = currentPage === totalPages;

    if (isFrontCover) {
      return { left: null, right: catalog?.pages?.[0] };
    } else if (isBackCover) {
      return { left: catalog?.pages?.[totalPages - 1], right: null };
    } else {
      const leftPageIndex = currentPage % 2 === 0 ? currentPage - 1 : currentPage - 2;
      const rightPageIndex = leftPageIndex + 1;
      return {
        left: catalog?.pages?.[leftPageIndex],
        right: rightPageIndex < totalPages ? catalog?.pages?.[rightPageIndex] : null
      };
    }
  }, [catalog?.pages, currentPage]);

  const { left: leftPageData, right: rightPageData } = useMemo(() => getDisplayPages(), [getDisplayPages]);

  // Preload adjacent pages
  useEffect(() => {
    if (!catalog?.pages) return;

    const preloadImage = (url) => {
      if (!url || preloadedImages.current.has(url)) return;
      const img = new window.Image();
      img.src = url;
      preloadedImages.current.set(url, img);
    };

    const totalPages = catalog?.pages?.length || 0;
    const isFrontCover = currentPage === 1;

    // Preload next pages
    if (currentPage < totalPages) {
      const nextPageNum = isFrontCover ? 2 : Math.min(totalPages, currentPage + 2);
      const nextPages = catalog?.pages?.filter((p, idx) => 
        idx === nextPageNum - 1 || idx === nextPageNum
      );
      nextPages?.forEach(page => preloadImage(page?.imageUrl));
    }

    // Preload previous pages
    if (currentPage > 1) {
      const prevPageNum = currentPage === 2 ? 1 : Math.max(2, currentPage - 2);
      const prevPages = catalog?.pages?.filter((p, idx) => 
        idx === prevPageNum - 1 || idx === prevPageNum - 2
      );
      prevPages?.forEach(page => preloadImage(page?.imageUrl));
    }
  }, [currentPage, catalog?.pages]);

  // Set page transition loading on page change
  useEffect(() => {
    if (catalog) {
      setImageLoading({ left: !!leftPageData, right: !!rightPageData });
      setIsPageTransitioning(true);
    }
  }, [currentPage, catalog, leftPageData, rightPageData]);

  // Clear page transition loader when all images are loaded
  useEffect(() => {
    const leftLoaded = !leftPageData || !imageLoading?.left;
    const rightLoaded = !rightPageData || !imageLoading?.right;
    
    if (leftLoaded && rightLoaded) {
      setIsPageTransitioning(false);
    }
  }, [imageLoading, leftPageData, rightPageData]);

  // Handle page turn with magazine layout
  const handlePageTurn = useCallback((direction) => {
    if (isFlipping) return;

    const totalPages = catalog?.pages?.length || 0;
    const isFrontCover = currentPage === 1;
    const isOddPageCatalog = totalPages % 2 !== 0;
    const isBackCover = currentPage === totalPages && !isOddPageCatalog;

    let targetPage;

    if (direction === 'next') {
      if (currentPage === totalPages) return;
      if (isFrontCover) {
        targetPage = 2;
      } else if (isOddPageCatalog) {
        if (currentPage === totalPages - 1) targetPage = totalPages;
        else if (currentPage % 2 === 0) targetPage = Math.min(totalPages, currentPage + 2);
        else targetPage = Math.min(totalPages, currentPage + 1);
      } else {
        if (currentPage === totalPages - 1 || currentPage === totalPages - 2) targetPage = totalPages;
        else targetPage = Math.min(totalPages, currentPage + 2);
      }
    } else {
      if (currentPage === 1) return;
      if (currentPage === 2) {
        targetPage = 1;
      } else if (isOddPageCatalog) {
        if (currentPage === totalPages) targetPage = totalPages - 1;
        else if (currentPage % 2 === 0) targetPage = Math.max(2, currentPage - 2);
        else targetPage = Math.max(2, currentPage - 1);
      } else {
        if (isBackCover) targetPage = totalPages - 2;
        else targetPage = Math.max(2, currentPage - 2);
      }
    }

    setFlipDirection(direction);
    setIsFlipping(true);

    // Para slider: pre-renderizar el spread entrante antes del timeout
    if (config?.viewerMode === 'slider') {
      setIncomingSpreadData(getSpreadForPage(targetPage));
    }

    const animDuration = config?.viewerMode === 'slider' ? 400 : 600;
    flipTimeoutRef.current = setTimeout(() => {
      setCurrentPage(targetPage);
      if (config?.viewerMode === 'slider') {
        requestAnimationFrame(() => {
          setIncomingSpreadData(null);
          setIsFlipping(false);
          setFlipDirection(null);
        });
      } else {
        setIsFlipping(false);
        setFlipDirection(null);
      }
    }, animDuration);
  }, [isFlipping, currentPage, catalog?.pages, config?.viewerMode, getSpreadForPage]);

  // Handle zoom
  const handleZoom = useCallback((action) => {
    if (action === 'in' && zoomLevel < 2) {
      setZoomLevel(prev => Math.min(prev + 0.25, 2));
    } else if (action === 'out' && zoomLevel > 0.5) {
      setZoomLevel(prev => Math.max(prev - 0.25, 0.5));
    } else if (action === 'reset') {
      setZoomLevel(1);
    }
  }, [zoomLevel]);

  // Handle fullscreen
  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      viewerRef?.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Handle hotspot click with deferred analytics
  const handleHotspotClick = useCallback((hotspot) => {
    const catalogId = catalog?.id;
    const companyId = catalog?.companyId;

    // Handle action immediately for instant UI response
    if (hotspot?.type === 'link') {
      window.open(hotspot?.url, '_blank', 'noopener,noreferrer');
    } else if (hotspot?.type === 'buy') {
      const sku = hotspot?.sku;
      if (!sku) return;
      
      // Open modal immediately - don't wait for analytics
      setSelectedProductSku(sku);
      setSelectedProductStoreUrl(hotspot?.vtexStoreUrl || null);
      setProductModalOpen(true);
    } else if (hotspot?.type === 'internal_link') {
      const targetPage = hotspot?.internalLinkPage;
      if (targetPage) {
        setCurrentPage(targetPage);
      }
    }

    // Defer analytics tracking to not block UI
    if (config?.analyticsEnabled && catalogId && companyId) {
      const deferTracking = window.requestIdleCallback || ((cb) => setTimeout(cb, 0));
      deferTracking(() => {
        widgetAnalyticsService?.trackHotspotClick(
          catalogId,
          hotspot?.pageNumber,
          hotspot?.type,
          hotspot?.id,
          companyId
        );

        if (hotspot?.type === 'buy' && hotspot?.sku) {
          widgetAnalyticsService?.trackBuyClick(
            catalogId,
            hotspot?.pageNumber,
            hotspot?.id,
            hotspot?.sku,
            hotspot?.quantity || 1,
            companyId
          );
        }
      });
    }
  }, [catalog, config?.analyticsEnabled]);

  // Memoized hotspot handlers
  const handleHotspotHover = useCallback((hotspotId) => {
    setHoveredHotspot(hotspotId);
  }, []);

  const handleHotspotLeave = useCallback(() => {
    setHoveredHotspot(null);
  }, []);

  // Handle share
  const [showShareMenu, setShowShareMenu] = useState(false);
  const shareMenuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (shareMenuRef.current && !shareMenuRef.current.contains(e.target)) {
        setShowShareMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getShareUrl = useCallback(() => {
    return `${window.location?.origin}/catalog/${catalog?.id}`;
  }, [catalog?.id]);

  const handleShareNetwork = useCallback((network) => {
    const url = getShareUrl();
    const shareLinks = {
      whatsapp: `https://wa.me/?text=${encodeURIComponent(url)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
      email: `mailto:?subject=${encodeURIComponent(catalog?.title || '')}&body=${encodeURIComponent(url)}`
    };
    if (network === 'copy') {
      navigator.clipboard?.writeText(url);
    } else {
      window.open(shareLinks[network], '_blank', 'noopener,noreferrer');
    }
    setShowShareMenu(false);
  }, [getShareUrl, catalog?.title]);

  // Memoized image load handlers
  const handleImageLoad = useCallback((side) => {
    setImageLoading(prev => ({ ...prev, [side]: false }));
  }, []);

  if (loading) {
    return (
      <div className="cf-loading">
        <div className="cf-spinner"></div>
        <p>{t('catalogViewer.loading')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="cf-error">
        <p>{t('catalogViewer.errors.loadFailed')}</p>
        <p className="cf-error-message">{error}</p>
      </div>
    );
  }

  if (!catalog) {
    return (
      <div className="cf-error">
        <p>{t('catalogViewer.errors.notFound')}</p>
      </div>
    );
  }

  const totalPages = catalog?.pages?.length || 0;
  const isFrontCover = currentPage === 1;
  const isBackCover = currentPage === totalPages;
  const theme = config?.theme || 'light';

  return (
    <div
      ref={viewerRef}
      className={`cf-widget cf-theme-${theme}`}
      style={{ width: config?.width, height: config?.height, touchAction: 'auto' }}
    >
      {/* Page Transition Loading Overlay */}
      {isPageTransitioning && (
        <div className="cf-page-transition-loader">
          <div className="cf-spinner"></div>
          <p>Loading page...</p>
        </div>
      )}

      {/* Main Flipbook Container */}
      <div className="cf-flipbook-container" style={{ transform: `scale(${zoomLevel})`, willChange: 'transform', position: 'relative', overflow: config?.viewerMode === 'slider' ? 'hidden' : 'visible' }}>
        {/* Spread actual */}
        <div
          className={`cf-spread ${
            isFlipping
              ? config?.viewerMode === 'slider'
                ? `cf-slide-${flipDirection}`
                : `cf-flip-${flipDirection}`
              : ''
          } ${isFrontCover || isBackCover ? 'cf-single-page' : 'cf-double-page'} ${
            config?.viewerMode === 'slider' && incomingSpreadData ? 'cf-spread-absolute' : ''
          }`}
          style={{ willChange: isFlipping ? 'transform' : 'auto' }}
        >
          {/* Left Page */}
          {leftPageData && (
            <div ref={(el) => { pageContainerRefs.current.left = el; }} className="cf-page cf-page-left">
              <img
                src={leftPageData?.imageUrl}
                alt={leftPageData?.imageAlt || `Page ${leftPageData?.pageNumber}`}
                className="cf-page-image"
                loading="eager"
                onLoad={() => handleImageLoad('left')}
                onError={() => handleImageLoad('left')}
              />
              {leftPageData?.hotspots?.map((hotspot) => (
                <WidgetHotspot
                  key={hotspot?.id}
                  hotspot={hotspot}
                  side="left"
                  pageContainerRefs={pageContainerRefs}
                  hoveredHotspot={hoveredHotspot}
                  onHover={() => handleHotspotHover(hotspot?.id)}
                  onLeave={handleHotspotLeave}
                  onClick={() => handleHotspotClick(hotspot)}
                />
              ))}
            </div>
          )}

          {/* Right Page */}
          {rightPageData && (
            <div ref={(el) => { pageContainerRefs.current.right = el; }} className="cf-page cf-page-right">
              <img
                src={rightPageData?.imageUrl}
                alt={rightPageData?.imageAlt || `Page ${rightPageData?.pageNumber}`}
                className="cf-page-image"
                loading="eager"
                onLoad={() => handleImageLoad('right')}
                onError={() => handleImageLoad('right')}
              />
              {rightPageData?.hotspots?.map((hotspot) => (
                <WidgetHotspot
                  key={hotspot?.id}
                  hotspot={hotspot}
                  side="right"
                  pageContainerRefs={pageContainerRefs}
                  hoveredHotspot={hoveredHotspot}
                  onHover={() => handleHotspotHover(hotspot?.id)}
                  onLeave={handleHotspotLeave}
                  onClick={() => handleHotspotClick(hotspot)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Spread entrante (solo slider mode) */}
        {config?.viewerMode === 'slider' && incomingSpreadData && (
          <div
            className={`cf-spread cf-spread-absolute ${
              isFrontCover || isBackCover ? 'cf-single-page' : 'cf-double-page'
            } ${flipDirection === 'next' ? 'cf-slide-in-right' : 'cf-slide-in-left'}`}
            style={{ willChange: 'transform' }}
          >
            {incomingSpreadData?.left && (
              <div className="cf-page cf-page-left">
                <img
                  src={incomingSpreadData.left?.imageUrl}
                  alt={`Page ${incomingSpreadData.left?.pageNumber}`}
                  className="cf-page-image"
                  loading="eager"
                />
              </div>
            )}
            {incomingSpreadData?.right && (
              <div className="cf-page cf-page-right">
                <img
                  src={incomingSpreadData.right?.imageUrl}
                  alt={`Page ${incomingSpreadData.right?.pageNumber}`}
                  className="cf-page-image"
                  loading="eager"
                />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Navigation Controls */}
      {config?.showNavigation && (
        <div className="cf-navigation">
          <button
            className="cf-nav-button cf-nav-prev"
            onClick={() => handlePageTurn('prev')}
            onTouchEnd={() => handlePageTurn('prev')}
            disabled={currentPage === 1 || isFlipping}
            aria-label="Previous page"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <polyline points="15 18 9 12 15 6"></polyline>
            </svg>
          </button>

          <div className="cf-page-counter">
            {isFrontCover ? 'Front Cover' : isBackCover ? 'Back Cover' : `Pages ${leftPageData?.pageNumber}-${rightPageData?.pageNumber}`} ({currentPage} / {totalPages})
          </div>

          <button
            className="cf-nav-button cf-nav-next"
            onClick={() => handlePageTurn('next')}
            onTouchEnd={() => handlePageTurn('next')}
            disabled={currentPage === totalPages || isFlipping}
            aria-label="Next page"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          </button>
        </div>
      )}

      {/* Zoom Controls */}
      {config?.showZoom && (
        <div className="cf-zoom-controls">
          <button
            className="cf-zoom-button"
            onClick={() => handleZoom('in')}
            disabled={zoomLevel >= 2}
            aria-label="Zoom in"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              <line x1="11" y1="8" x2="11" y2="14"></line>
              <line x1="8" y1="11" x2="14" y2="11"></line>
            </svg>
          </button>
          <button
            className="cf-zoom-button"
            onClick={() => handleZoom('out')}
            disabled={zoomLevel <= 0.5}
            aria-label="Zoom out"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              <line x1="8" y1="11" x2="14" y2="11"></line>
            </svg>
          </button>
          <button
            className="cf-zoom-button"
            onClick={() => handleZoom('reset')}
            disabled={zoomLevel === 1}
            aria-label="Reset zoom"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <polyline points="1 4 1 10 7 10"></polyline>
              <polyline points="23 20 23 14 17 14"></polyline>
              <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15"></path>
            </svg>
          </button>
        </div>
      )}

      {/* Fullscreen Toggle 
      {config?.showFullscreen && (
        <button
          className="cf-fullscreen-button"
          onClick={toggleFullscreen}
          aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
        >
          {isFullscreen ? (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3"></path>
            </svg>
          ) : (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"></path>
            </svg>
          )}
        </button>
      )}

      {/* Share Button */}
      {config?.showSharing && (
        <div className="cf-share-wrapper" ref={shareMenuRef}>
          <button
            className="cf-share-button"
            onClick={() => setShowShareMenu(prev => !prev)}
            aria-label="Share catalog"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <circle cx="18" cy="5" r="3"></circle>
              <circle cx="6" cy="12" r="3"></circle>
              <circle cx="18" cy="19" r="3"></circle>
              <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
              <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
            </svg>
          </button>
          {showShareMenu && (
            <div className="cf-share-menu">
              <button className="cf-share-option" onClick={() => handleShareNetwork('whatsapp')}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style={{color:'#25D366'}}><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.125.558 4.122 1.532 5.856L0 24l6.335-1.54A11.945 11.945 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.848 0-3.568-.5-5.051-1.37l-.361-.214-3.762.916.948-3.658-.235-.375A9.953 9.953 0 012 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z"/></svg>
                WhatsApp
              </button>
              <button className="cf-share-option" onClick={() => handleShareNetwork('facebook')}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style={{color:'#1877F2'}}><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                Facebook
              </button>
              <button className="cf-share-option" onClick={() => handleShareNetwork('twitter')}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.73-8.835L1.254 2.25H8.08l4.253 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                X (Twitter)
              </button>
              <button className="cf-share-option" onClick={() => handleShareNetwork('linkedin')}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style={{color:'#0A66C2'}}><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                LinkedIn
              </button>
              <button className="cf-share-option" onClick={() => handleShareNetwork('email')}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="4" width="20" height="16" rx="2"/><polyline points="22,4 12,13 2,4"/></svg>
                Email
              </button>
              <div className="cf-share-divider"></div>
              <button className="cf-share-option" onClick={() => handleShareNetwork('copy')}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>
                Copiar enlace
              </button>
            </div>
          )}
        </div>
      )}

      {/* Branding */}
      <div className="cf-branding">
        <span>Powered by CatalogFlip</span>
      </div>

      {/* Product Modal */}
      <ProductModal
        isOpen={productModalOpen}
        onClose={() => {
          setProductModalOpen(false);
          setSelectedProductSku(null);
          setSelectedProductStoreUrl(null);
        }}
        skuId={selectedProductSku}
        storeUrl={selectedProductStoreUrl}
        vtexAccountName={catalog?.company?.vtexAccountName}
        companyId={catalog?.companyId}
        isEmbed={true}
      />
    </div>
  );
};

export default EmbeddableFlipbook;